import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { Navbar } from "../components/Navbar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_URL, API_BASE_URL } from "../config/api";
import {
  MessageSquare,
  Users,
  Clock,
  CheckCircle,
  Send,
  Loader2,
  Phone,
  Mail,
  Building,
  ArrowLeft,
  UserCheck,
  Circle,
  RefreshCw,
  Megaphone,
  UserPlus,
  AlertTriangle,
  Info,
  AlertCircle,
  X,
  Forward,
} from "lucide-react";


export default function SupportQueuePage() {
  const [sessions, setSessions] = useState([]);
  const [selectedSession, setSelectedSession] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [joining, setJoining] = useState(false);
  const { getAuthHeaders, user, token } = useAuth();
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  // Broadcast notification state
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastForm, setBroadcastForm] = useState({
    title: "",
    message: "",
    type: "info",
    expires_in_hours: 24
  });
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // Task assignment state
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [taskForm, setTaskForm] = useState({
    session_id: "",
    assigned_to: "",
    task_title: "",
    task_description: "",
    priority: "medium",
    due_date: ""
  });
  const [staffList, setStaffList] = useState([]);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [assigningTask, setAssigningTask] = useState(false);

  // Follow-up email state
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [followupMessage, setFollowupMessage] = useState("");
  const [sendingFollowup, setSendingFollowup] = useState(false);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Fetch support queue
  const fetchQueue = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/live-support/queue`, {
        headers: getAuthHeaders(),
      });
      setSessions(response.data.sessions);
    } catch (error) {
      console.error("Failed to fetch queue:", error);
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, [fetchQueue]);

  // WebSocket connection for real-time updates
  useEffect(() => {
    if (!token) return;

    const wsUrl = `${API_BASE_URL.replace('https://', 'wss://').replace('http://', 'ws://')}/api/live-support/ws/staff?token=${token}`;
    
    const connectWebSocket = () => {
      try {
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log("Staff WebSocket connected");
        };
        
        ws.onmessage = (event) => {
          const data = JSON.parse(event.data);
          
          if (data.type === "queue_update") {
            fetchQueue();
          } else if (data.type === "new_message" && selectedSession?.id === data.message?.session_id) {
            setMessages(prev => [...prev, data.message]);
          } else if (data.type === "new_customer_message") {
            fetchQueue(); // Update unread counts
            if (selectedSession?.id === data.session_id) {
              // Refresh messages for current session
              loadSessionDetails(data.session_id);
            }
          } else if (data.type === "customer_online" || data.type === "customer_offline") {
            fetchQueue();
          }
        };
        
        ws.onclose = () => {
          console.log("Staff WebSocket disconnected, reconnecting...");
          setTimeout(connectWebSocket, 3000);
        };
        
        ws.onerror = (error) => {
          console.error("WebSocket error:", error);
        };
        
        wsRef.current = ws;
      } catch (error) {
        console.error("WebSocket connection error:", error);
      }
    };
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [token, selectedSession, fetchQueue]);

  // Load session details
  const loadSessionDetails = async (sessionId) => {
    try {
      const response = await axios.get(
        `${API_URL}/live-support/session/${sessionId}`,
        { headers: getAuthHeaders() }
      );
      setSelectedSession(response.data.session);
      setMessages(response.data.messages);
      
      // Tell WebSocket we're watching this session
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({
          type: "watch_session",
          session_id: sessionId
        }));
      }
    } catch (error) {
      toast.error("Failed to load session details");
    }
  };

  // Join session
  const handleJoinSession = async () => {
    if (!selectedSession) return;
    
    setJoining(true);
    try {
      await axios.post(
        `${API_URL}/live-support/join`,
        { session_id: selectedSession.id },
        { headers: getAuthHeaders() }
      );
      toast.success("You have joined the chat");
      loadSessionDetails(selectedSession.id);
      fetchQueue();
    } catch (error) {
      toast.error("Failed to join session");
    } finally {
      setJoining(false);
    }
  };

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedSession) return;
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API_URL}/live-support/send-message`,
        {
          session_id: selectedSession.id,
          content: newMessage.trim()
        },
        { headers: getAuthHeaders() }
      );
      
      setMessages(prev => [...prev, response.data.message]);
      setNewMessage("");
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  // Resolve session
  const handleResolve = async () => {
    if (!selectedSession) return;
    
    try {
      await axios.post(
        `${API_URL}/live-support/resolve/${selectedSession.id}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success("Session resolved");
      setSelectedSession(null);
      setMessages([]);
      fetchQueue();
    } catch (error) {
      toast.error("Failed to resolve session");
    }
  };

  // Fetch staff list for task assignment
  const fetchStaffList = async () => {
    setLoadingStaff(true);
    try {
      const response = await axios.get(`${API_URL}/live-support/staff-list`, {
        headers: getAuthHeaders()
      });
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error("Failed to fetch staff:", error);
      toast.error("Failed to load staff list");
    } finally {
      setLoadingStaff(false);
    }
  };

  // Send broadcast notification
  const handleSendBroadcast = async () => {
    if (!broadcastForm.title.trim() || !broadcastForm.message.trim()) {
      toast.error("Please fill in all required fields");
      return;
    }

    setSendingBroadcast(true);
    try {
      await axios.post(
        `${API_URL}/live-support/broadcast`,
        broadcastForm,
        { headers: getAuthHeaders() }
      );
      toast.success("Broadcast notification sent to all users!");
      setShowBroadcastModal(false);
      setBroadcastForm({
        title: "",
        message: "",
        type: "info",
        expires_in_hours: 24
      });
    } catch (error) {
      toast.error("Failed to send broadcast");
    } finally {
      setSendingBroadcast(false);
    }
  };

  // Open task assignment modal
  const handleOpenTaskModal = (session = null) => {
    if (session) {
      setTaskForm(prev => ({
        ...prev,
        session_id: session.id,
        task_title: `Follow up: ${session.customer_name || "Customer"}`,
        task_description: ""
      }));
    }
    fetchStaffList();
    setShowTaskModal(true);
  };

  // Assign task
  const handleAssignTask = async () => {
    if (!taskForm.assigned_to || !taskForm.task_title.trim()) {
      toast.error("Please select a staff member and provide a task title");
      return;
    }

    if (!taskForm.session_id) {
      toast.error("No session selected for task assignment");
      return;
    }

    setAssigningTask(true);
    try {
      await axios.post(
        `${API_URL}/live-support/assign-task`,
        {
          session_id: taskForm.session_id,
          assigned_to: taskForm.assigned_to,
          task_title: taskForm.task_title,
          task_description: taskForm.task_description,
          priority: taskForm.priority,
          due_date: taskForm.due_date || null
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Task assigned successfully!");
      setShowTaskModal(false);
      setTaskForm({
        session_id: "",
        assigned_to: "",
        task_title: "",
        task_description: "",
        priority: "medium",
        due_date: ""
      });
      // Refresh session to show system message
      if (selectedSession) {
        loadSessionDetails(selectedSession.id);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign task");
    } finally {
      setAssigningTask(false);
    }
  };

  // Send follow-up email
  const handleSendFollowup = async () => {
    if (!selectedSession) return;
    
    setSendingFollowup(true);
    try {
      await axios.post(
        `${API_URL}/live-support/followup/send`,
        {
          session_id: selectedSession.id,
          custom_message: followupMessage || null
        },
        { headers: getAuthHeaders() }
      );
      toast.success("Follow-up email sent! The customer will receive it shortly.");
      setShowFollowupModal(false);
      setFollowupMessage("");
      // Refresh to show system message
      loadSessionDetails(selectedSession.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send follow-up email");
    } finally {
      setSendingFollowup(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "pending":
        return <Badge variant="secondary" className="bg-yellow-500/20 text-yellow-600">Pending</Badge>;
      case "active":
        return <Badge variant="secondary" className="bg-green-500/20 text-green-600">Active</Badge>;
      case "resolved":
        return <Badge variant="secondary" className="bg-gray-500/20 text-gray-600">Resolved</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return "Just now";
    if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
    if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      
      <main className="flex-1 pt-16">
        <div className="h-[calc(100vh-4rem)] flex">
          {/* Sidebar - Session List */}
          <div className="w-80 border-r border-border/50 flex flex-col bg-card/50">
            <div className="p-4 border-b border-border/50">
              <div className="flex items-center justify-between mb-2">
                <h2 className="font-heading font-bold text-lg flex items-center gap-2">
                  <Users className="h-5 w-5 text-primary" />
                  Support Queue
                </h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchQueue}
                  data-testid="refresh-queue-btn"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
              <p className="text-sm text-muted-foreground mb-3">
                {sessions.filter(s => s.status === "pending").length} pending,{" "}
                {sessions.filter(s => s.status === "active").length} active
              </p>
              
              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => setShowBroadcastModal(true)}
                  data-testid="broadcast-btn"
                >
                  <Megaphone className="h-4 w-4 mr-1" />
                  Broadcast
                </Button>
              </div>
            </div>
            
            <ScrollArea className="flex-1">
              <div className="p-2 space-y-2">
                <AnimatePresence>
                  {sessions.length === 0 ? (
                    <div className="p-4 text-center text-muted-foreground">
                      <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No support requests</p>
                    </div>
                  ) : (
                    sessions.map((session) => (
                      <motion.div
                        key={session.id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                      >
                        <Card
                          className={`cursor-pointer transition-all hover:border-primary/50 ${
                            selectedSession?.id === session.id
                              ? "border-primary bg-primary/5"
                              : ""
                          }`}
                          onClick={() => loadSessionDetails(session.id)}
                          data-testid={`session-card-${session.id}`}
                        >
                          <CardContent className="p-3">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate">
                                    {session.customer_name}
                                  </span>
                                  {session.is_online && (
                                    <Circle className="h-2 w-2 fill-green-500 text-green-500" />
                                  )}
                                </div>
                                <p className="text-xs text-muted-foreground truncate">
                                  {session.customer_email}
                                </p>
                              </div>
                              <div className="flex flex-col items-end gap-1">
                                {getStatusBadge(session.status)}
                                {session.unread_count > 0 && (
                                  <Badge className="bg-primary text-primary-foreground text-xs">
                                    {session.unread_count}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            {session.description && (
                              <p className="text-xs text-muted-foreground mt-2 line-clamp-2">
                                {session.description}
                              </p>
                            )}
                            <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                {formatDate(session.created_at)}
                              </span>
                              {session.assigned_staff_name && (
                                <span className="flex items-center gap-1">
                                  <UserCheck className="h-3 w-3" />
                                  {session.assigned_staff_name.split(" ")[0]}
                                </span>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      </motion.div>
                    ))
                  )}
                </AnimatePresence>
              </div>
            </ScrollArea>
          </div>
          
          {/* Main Content - Chat View */}
          <div className="flex-1 flex flex-col">
            {selectedSession ? (
              <>
                {/* Session Header */}
                <div className="p-4 border-b border-border/50 bg-card/50">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedSession(null);
                          setMessages([]);
                        }}
                        className="lg:hidden"
                      >
                        <ArrowLeft className="h-4 w-4" />
                      </Button>
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-heading font-bold">
                            {selectedSession.customer_name}
                          </h3>
                          {selectedSession.is_online && (
                            <span className="flex items-center gap-1 text-xs text-green-600">
                              <Circle className="h-2 w-2 fill-green-500" />
                              Online
                            </span>
                          )}
                          {getStatusBadge(selectedSession.status)}
                        </div>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1">
                          <span className="flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            {selectedSession.customer_email}
                          </span>
                          {selectedSession.customer_phone && (
                            <span className="flex items-center gap-1">
                              <Phone className="h-3 w-3" />
                              {selectedSession.customer_phone}
                            </span>
                          )}
                          {selectedSession.company && (
                            <span className="flex items-center gap-1">
                              <Building className="h-3 w-3" />
                              {selectedSession.company}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {selectedSession.status === "pending" && (
                        <Button
                          onClick={handleJoinSession}
                          disabled={joining}
                          data-testid="join-chat-btn"
                        >
                          {joining ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <UserCheck className="h-4 w-4 mr-2" />
                          )}
                          Join Chat
                        </Button>
                      )}
                      {selectedSession.status === "active" && (
                        <>
                          <Button
                            variant="outline"
                            onClick={() => setShowFollowupModal(true)}
                            data-testid="send-followup-btn"
                          >
                            <Forward className="h-4 w-4 mr-2" />
                            Send Follow-up
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => handleOpenTaskModal(selectedSession)}
                            data-testid="assign-task-btn"
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            Assign Task
                          </Button>
                          <Button
                            variant="outline"
                            onClick={handleResolve}
                            data-testid="resolve-session-btn"
                          >
                            <CheckCircle className="h-4 w-4 mr-2" />
                            Resolve
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                  
                  {selectedSession.description && (
                    <div className="mt-3 p-3 bg-secondary/50 rounded-lg">
                      <p className="text-sm font-medium text-muted-foreground mb-1">Customer's Request:</p>
                      <p className="text-sm">{selectedSession.description}</p>
                    </div>
                  )}
                </div>
                
                {/* Messages Area */}
                <ScrollArea className="flex-1 p-4">
                  <div className="space-y-4 max-w-3xl mx-auto">
                    {messages.map((message, index) => (
                      <motion.div
                        key={message.id || index}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className={`flex ${
                          message.role === "user"
                            ? "justify-start"
                            : message.role === "system"
                            ? "justify-center"
                            : "justify-end"
                        }`}
                      >
                        {message.role === "system" ? (
                          <div className="px-4 py-2 bg-secondary/50 rounded-full text-xs text-muted-foreground">
                            {message.content}
                          </div>
                        ) : (
                          <div
                            className={`max-w-[70%] p-3 rounded-lg ${
                              message.role === "user"
                                ? "bg-secondary"
                                : message.role === "staff"
                                ? "bg-primary text-primary-foreground"
                                : "bg-blue-500/20 text-blue-900 dark:text-blue-100"
                            }`}
                          >
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium opacity-80">
                                {message.role === "user"
                                  ? selectedSession.customer_name
                                  : message.role === "assistant"
                                  ? "Roket (AI)"
                                  : message.sender_name || "Staff"}
                              </span>
                              <span className="text-xs opacity-60">
                                {formatTime(message.timestamp)}
                              </span>
                            </div>
                            <p className="text-sm whitespace-pre-wrap">
                              {message.content}
                            </p>
                          </div>
                        )}
                      </motion.div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                </ScrollArea>
                
                {/* Message Input */}
                {selectedSession.status === "active" && (
                  <div className="p-4 border-t border-border/50 bg-card/50">
                    <form onSubmit={handleSendMessage} className="flex gap-3 max-w-3xl mx-auto">
                      <Input
                        type="text"
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        disabled={sending}
                        className="flex-1"
                        data-testid="staff-message-input"
                      />
                      <Button
                        type="submit"
                        disabled={sending || !newMessage.trim()}
                        data-testid="send-staff-message-btn"
                      >
                        {sending ? (
                          <Loader2 className="h-5 w-5 animate-spin" />
                        ) : (
                          <Send className="h-5 w-5" />
                        )}
                      </Button>
                    </form>
                  </div>
                )}
                
                {selectedSession.status === "pending" && (
                  <div className="p-4 border-t border-border/50 bg-yellow-500/10 text-center">
                    <p className="text-sm text-muted-foreground">
                      Click "Join Chat" to take over this conversation and start messaging the customer.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center">
                <div className="text-center">
                  <MessageSquare className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
                  <h3 className="font-heading font-bold text-lg mb-2">
                    Select a Conversation
                  </h3>
                  <p className="text-muted-foreground">
                    Choose a support request from the queue to view details and chat history.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Broadcast Notification Modal */}
      <Dialog open={showBroadcastModal} onOpenChange={setShowBroadcastModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Megaphone className="h-5 w-5 text-primary" />
              Send Broadcast Notification
            </DialogTitle>
            <DialogDescription>
              This message will be shown as a popup to all users in the application.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Title *</label>
              <Input
                placeholder="e.g., System Maintenance Notice"
                value={broadcastForm.title}
                onChange={(e) => setBroadcastForm(prev => ({ ...prev, title: e.target.value }))}
                data-testid="broadcast-title-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Message *</label>
              <Textarea
                placeholder="Enter the message you want to broadcast..."
                rows={4}
                value={broadcastForm.message}
                onChange={(e) => setBroadcastForm(prev => ({ ...prev, message: e.target.value }))}
                data-testid="broadcast-message-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Type</label>
                <Select
                  value={broadcastForm.type}
                  onValueChange={(value) => setBroadcastForm(prev => ({ ...prev, type: value }))}
                >
                  <SelectTrigger data-testid="broadcast-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="info">
                      <span className="flex items-center gap-2">
                        <Info className="h-4 w-4 text-blue-500" />
                        Info
                      </span>
                    </SelectItem>
                    <SelectItem value="warning">
                      <span className="flex items-center gap-2">
                        <AlertTriangle className="h-4 w-4 text-yellow-500" />
                        Warning
                      </span>
                    </SelectItem>
                    <SelectItem value="urgent">
                      <span className="flex items-center gap-2">
                        <AlertCircle className="h-4 w-4 text-red-500" />
                        Urgent
                      </span>
                    </SelectItem>
                    <SelectItem value="success">
                      <span className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4 text-green-500" />
                        Success
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Expires After</label>
                <Select
                  value={String(broadcastForm.expires_in_hours)}
                  onValueChange={(value) => setBroadcastForm(prev => ({ ...prev, expires_in_hours: parseInt(value) }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">1 hour</SelectItem>
                    <SelectItem value="6">6 hours</SelectItem>
                    <SelectItem value="24">24 hours</SelectItem>
                    <SelectItem value="72">3 days</SelectItem>
                    <SelectItem value="168">7 days</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowBroadcastModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendBroadcast} disabled={sendingBroadcast}>
              {sendingBroadcast ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Megaphone className="h-4 w-4 mr-2" />
              )}
              Send Broadcast
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Task Modal */}
      <Dialog open={showTaskModal} onOpenChange={setShowTaskModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5 text-primary" />
              Assign Task to Staff
            </DialogTitle>
            <DialogDescription>
              Create a follow-up task from this support conversation and assign it to a team member.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign To *</label>
              {loadingStaff ? (
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Loading staff...
                </div>
              ) : (
                <Select
                  value={taskForm.assigned_to}
                  onValueChange={(value) => setTaskForm(prev => ({ ...prev, assigned_to: value }))}
                >
                  <SelectTrigger data-testid="task-assign-select">
                    <SelectValue placeholder="Select staff member" />
                  </SelectTrigger>
                  <SelectContent>
                    {staffList.map((staff) => (
                      <SelectItem key={staff.id} value={staff.id}>
                        <span className="flex items-center gap-2">
                          <span>{staff.name}</span>
                          {staff.department && (
                            <Badge variant="secondary" className="text-xs">
                              {staff.department}
                            </Badge>
                          )}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Task Title *</label>
              <Input
                placeholder="e.g., Follow up on shipping inquiry"
                value={taskForm.task_title}
                onChange={(e) => setTaskForm(prev => ({ ...prev, task_title: e.target.value }))}
                data-testid="task-title-input"
              />
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Description</label>
              <Textarea
                placeholder="Provide details about the task..."
                rows={3}
                value={taskForm.task_description}
                onChange={(e) => setTaskForm(prev => ({ ...prev, task_description: e.target.value }))}
                data-testid="task-description-input"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Priority</label>
                <Select
                  value={taskForm.priority}
                  onValueChange={(value) => setTaskForm(prev => ({ ...prev, priority: value }))}
                >
                  <SelectTrigger data-testid="task-priority-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="low">
                      <span className="flex items-center gap-2">
                        <Circle className="h-3 w-3 fill-gray-400 text-gray-400" />
                        Low
                      </span>
                    </SelectItem>
                    <SelectItem value="medium">
                      <span className="flex items-center gap-2">
                        <Circle className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        Medium
                      </span>
                    </SelectItem>
                    <SelectItem value="high">
                      <span className="flex items-center gap-2">
                        <Circle className="h-3 w-3 fill-orange-500 text-orange-500" />
                        High
                      </span>
                    </SelectItem>
                    <SelectItem value="urgent">
                      <span className="flex items-center gap-2">
                        <Circle className="h-3 w-3 fill-red-500 text-red-500" />
                        Urgent
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium">Due Date</label>
                <Input
                  type="date"
                  value={taskForm.due_date}
                  onChange={(e) => setTaskForm(prev => ({ ...prev, due_date: e.target.value }))}
                  data-testid="task-due-date-input"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTaskModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleAssignTask} disabled={assigningTask}>
              {assigningTask ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <UserPlus className="h-4 w-4 mr-2" />
              )}
              Assign Task
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Follow-up Email Modal */}
      <Dialog open={showFollowupModal} onOpenChange={setShowFollowupModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Forward className="h-5 w-5 text-primary" />
              Send Follow-up Email
            </DialogTitle>
            <DialogDescription>
              Continue the conversation via email. This is useful when the customer leaves the chat or you need to follow up later.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedSession && (
              <div className="p-3 bg-secondary/50 rounded-lg">
                <p className="text-sm">
                  <strong>To:</strong> {selectedSession.contact_email || selectedSession.user_email || "Customer email"}
                </p>
                <p className="text-sm mt-1">
                  <strong>Customer:</strong> {selectedSession.customer_name || selectedSession.user_name}
                </p>
              </div>
            )}

            <div className="space-y-2">
              <label className="text-sm font-medium">Custom Message (Optional)</label>
              <Textarea
                placeholder="Add a personal message... If left blank, a summary of the conversation will be sent automatically."
                rows={4}
                value={followupMessage}
                onChange={(e) => setFollowupMessage(e.target.value)}
                data-testid="followup-message-input"
              />
              <p className="text-xs text-muted-foreground">
                The email will include the conversation summary and invite the customer to reply via email.
              </p>
            </div>

            <div className="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex gap-2">
                <Info className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p className="text-xs text-blue-800 dark:text-blue-200">
                  <strong>Auto Follow-up:</strong> When customers disconnect without resolution, the system automatically sends a follow-up email to continue the conversation.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowFollowupModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendFollowup} disabled={sendingFollowup}>
              {sendingFollowup ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              Send Email
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export { SupportQueuePage };
